/**
 * Page de création d'une nouvelle exécution
 *
 * Fonctionnalités:
 * - Sélection architecture / version
 * - Sélection environnement
 * - Choix de l'action (plan / apply / destroy)
 * - Variables Terraform configurables
 * - Validation avant création
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganizationStatus } from '../../hooks/useOrganizationStatus';
import DashboardLayout from '../Dashboard/DashboardLayout';
import {
  createPlanExecution,
  createApplyExecution,
  createDestroyExecution,
  listExecutions,
  resetTfstate,
} from '../../api/executions';
import { listEnvironments, type Environment } from '../../api/environments';
import { getVersionHistory, getLatestVersion, type ArchitectureVersion } from '../../api/versioning';
import * as variablesApi from '../../api/variables';
import * as architecturesApi from '../../api/architectures';
import * as projectsApi from '../../api/projects';
import './NewExecutionPage.css';

const BLOCKED_STATUSES = ['TRIAL_EXPIRED', 'SUSPENDED', 'CANCELLED'];

const NewExecutionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organization, loading: orgLoading } = useOrganizationStatus();

  // Pre-filled from URL params
  const prefilledArchitectureId = searchParams.get('architecture_id');
  const prefilledAction = searchParams.get('action') as 'plan' | 'apply' | 'destroy' | null;
  const prefilledPlanRunId = searchParams.get('plan_run_id');
  const prefilledVersionId = searchParams.get('version_id');
  const prefilledEnvironmentId = searchParams.get('environment_id');

  // Form state
  const [action, setAction] = useState<'plan' | 'apply' | 'destroy'>(prefilledAction || 'plan');
  const [architectureId, setArchitectureId] = useState(prefilledArchitectureId || '');
  const [versionId, setVersionId] = useState(prefilledVersionId || '');
  const [environmentId, setEnvironmentId] = useState(prefilledEnvironmentId || '');
  const [planRunId, setPlanRunId] = useState(prefilledPlanRunId || ''); // For apply
  const [variables, setVariables] = useState<Record<string, any>>({});

  // Parameters resolution
  const [resolvedParameters, setResolvedParameters] = useState<variablesApi.ResolvedParameter[]>([]);
  const [executionOverrides, setExecutionOverrides] = useState<Record<string, any>>({});
  const [parametersLoading, setParametersLoading] = useState(false);

  // Data
  const [projects, setProjects] = useState<projectsApi.Project[]>([]);
  const [architectures, setArchitectures] = useState<architecturesApi.Architecture[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [versions, setVersions] = useState<ArchitectureVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [recentPlans, setRecentPlans] = useState<any[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [destroyConfirmText, setDestroyConfirmText] = useState('');
  const [validationErrors, setValidationErrors] = useState<Array<{label: string, resourceType: string, field?: string, message: string}> | null>(null);
  const [variablesText, setVariablesText] = useState('{}');
  const [architectureSearchTerm, setArchitectureSearchTerm] = useState('');
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  const [showArchitectureDropdown, setShowArchitectureDropdown] = useState(false);

  useEffect(() => {
    if (!orgLoading && organization && BLOCKED_STATUSES.includes(organization.status?.toUpperCase().replace(/-/g, '_') || '')) {
      navigate('/settings/subscription');
    }
  }, [orgLoading, organization, navigate]);

  /**
   * Charger les projets
   */
  const loadProjects = async () => {
    try {
      const data = await projectsApi.getProjects();
      setProjects(data);
    } catch (err: any) {
      console.error('Failed to load projects:', err);
    }
  };

  /**
   * Charger les architectures
   * Utilise getAllArchitectures (limit=1000) pour retourner TOUTES les archs de l'orga,
   * pas seulement les 10 plus récentes comme getRecentArchitectures.
   */
  const loadArchitectures = async () => {
    try {
      const data = await architecturesApi.getAllArchitectures();
      setArchitectures(data);
    } catch (err: any) {
      console.error('Failed to load architectures:', err);
    }
  };

  /**
   * Charger les environnements
   */
  const loadEnvironments = async () => {
    try {
      const data = await listEnvironments();
      setEnvironments(data);
    } catch (err: any) {
      console.error('Failed to load environments:', err);
    }
  };

  /**
   * Charger les versions d'une architecture
   */
  const loadVersions = async (archId: string) => {
    try {
      setVersionsLoading(true);
      setVersions([]);
      const data = await getVersionHistory(archId, { limit: 20 });
      setVersions(data);

      // Auto-select latest version
      if (data.length > 0 && !versionId) {
        setVersionId(data[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load versions:', err);
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  /**
   * Charger les plans récents (pour apply)
   */
  const loadRecentPlans = async () => {
    if (!architectureId || !environmentId) return;

    try {
      const data = await listExecutions({
        architectureId: architectureId,
        environmentId: environmentId,
        action: 'plan',
        status: 'success',
        limit: 10
      });
      setRecentPlans(data.executions);
    } catch (err: any) {
      console.error('Failed to load recent plans:', err);
    }
  };

  /**
   * Charger les paramètres résolus
   */
  const loadResolvedParameters = async () => {
    if (!architectureId) return;

    try {
      setParametersLoading(true);

      const result = await variablesApi.previewParameters(
        architectureId,
        environmentId || undefined,
        executionOverrides
      );

      setResolvedParameters(result.parameters);
    } catch (err: any) {
      console.error('Failed to load resolved parameters:', err);
    } finally {
      setParametersLoading(false);
    }
  };

  /**
   * Effect: Charger les données initiales au montage
   */
  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      await Promise.all([
        loadProjects(),
        loadArchitectures(),
        loadEnvironments()
      ]);
      setInitialLoading(false);
    };

    loadInitialData();
  }, []);

  /**
   * Effect: Charger les versions quand l'architecture change
   */
  useEffect(() => {
    if (architectureId) {
      loadVersions(architectureId);
    } else {
      setVersions([]);
      setVersionId('');
    }
  }, [architectureId]);

  /**
   * Effect: Charger les plans récents pour apply
   */
  useEffect(() => {
    if (action === 'apply' && architectureId && environmentId) {
      loadRecentPlans();
    }
  }, [action, architectureId, environmentId]);

  /**
   * Effect: Charger les paramètres résolus
   */
  useEffect(() => {
    if (architectureId) {
      loadResolvedParameters();
    }
  }, [architectureId, environmentId]);

  /**
   * Effect: Fermer la dropdown quand on clique à l'extérieur
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showArchitectureDropdown && !target.closest('.form-group')) {
        setShowArchitectureDropdown(false);
        setArchitectureSearchTerm('');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showArchitectureDropdown]);

  /**
   * Parser les variables JSON
   */
  const parseVariables = () => {
    try {
      const parsed = JSON.parse(variablesText);
      setVariables(parsed);
      setError(null);
      setValidationErrors(null);
      return parsed;
    } catch (err) {
      setError('Invalid JSON in variables');
      return null;
    }
  };

  /**
   * Valider le formulaire
   */
  const validateForm = (): boolean => {
    if (!architectureId) {
      setError('Please select an architecture');
      return false;
    }
    if (!versionId) {
      setError('Please select a version');
      return false;
    }
    if (!environmentId) {
      setError('Please select an environment');
      return false;
    }
    if (action === 'apply' && !planRunId) {
      setError('Please select a plan run for apply');
      return false;
    }

    if (action === 'destroy') {
      const archName = architectures.find(a => a.id === architectureId)?.name || '';
      if (destroyConfirmText !== archName) {
        setError(`Type the architecture name "${archName}" to confirm destruction`);
        return false;
      }
    }

    return true;
  };

  /**
   * Soumettre le formulaire
   */
  const [resetStateLoading, setResetStateLoading] = useState(false);
  const [resetStateMessage, setResetStateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleResetTfstate = async () => {
    if (!architectureId || !environmentId) return;
    const archName = architectures.find(a => a.id === architectureId)?.name || architectureId;
    const envName  = environments.find(e => e.id === environmentId)?.name || environmentId;
    if (!window.confirm(`Réinitialiser le Terraform state de "${archName}" sur l'environnement "${envName}" ?\n\nCette opération supprime le tfstate Azure Storage. Terraform recréera toutes les ressources au prochain Apply.`)) return;
    setResetStateLoading(true);
    setResetStateMessage(null);
    try {
      const result = await resetTfstate(architectureId, environmentId);
      setResetStateMessage({ type: 'success', text: result.message });
    } catch (err: any) {
      setResetStateMessage({ type: 'error', text: err?.response?.data?.error || err.message });
    } finally {
      setResetStateLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        architecture_id: architectureId,
        environment_id: environmentId,
        version_id: versionId
      };

      // Ajouter les overrides d'exécution si présents
      if (Object.keys(executionOverrides).length > 0) {
        payload.parameter_overrides = executionOverrides;
      }

      if (action === 'apply') {
        payload.plan_run_id = planRunId;
      }

      let execution;
      switch (action) {
        case 'plan':
          execution = await createPlanExecution(payload);
          break;
        case 'apply':
          execution = await createApplyExecution(payload);
          break;
        case 'destroy':
          execution = await createDestroyExecution(payload);
          break;
      }

      // Navigate to the execution details
      navigate(`/executions/${execution.run_id}`);
    } catch (err: any) {
      if (err.response?.data?.code === 'ARCHITECTURE_VALIDATION_FAILED') {
        setValidationErrors(err.response.data.validationErrors || []);
        setError(null);
      } else if (err.response?.data?.code === 'MISSING_CONNECTION_SERVICE') {
        setError(`⚠️ ${err.response.data.error}`);
        setValidationErrors(null);
      } else {
        setError(err.response?.data?.error || `Failed to create ${action} execution`);
        setValidationErrors(null);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle project expansion
   */
  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  /**
   * Grouper les architectures par projet avec filtrage
   */
  const architecturesByProject = projects.reduce((acc, project) => {
    const projectArchs = architectures.filter(arch => {
      const matchesProject = arch.project_id === project.id;
      const matchesSearch = architectureSearchTerm === '' ||
        arch.name.toLowerCase().includes(architectureSearchTerm.toLowerCase()) ||
        arch.provider.toLowerCase().includes(architectureSearchTerm.toLowerCase());
      return matchesProject && matchesSearch;
    });
    if (projectArchs.length > 0) {
      acc[project.id] = {
        project,
        architectures: projectArchs
      };
    }
    return acc;
  }, {} as Record<string, { project: projectsApi.Project; architectures: architecturesApi.Architecture[] }>);

  // Architectures sans projet avec filtrage
  const architecturesWithoutProject = architectures.filter(arch => {
    const noProject = !projects.some(p => p.id === arch.project_id);
    const matchesSearch = architectureSearchTerm === '' ||
      arch.name.toLowerCase().includes(architectureSearchTerm.toLowerCase()) ||
      arch.provider.toLowerCase().includes(architectureSearchTerm.toLowerCase());
    return noProject && matchesSearch;
  });

  /**
   * Obtenir l'architecture sélectionnée
   */
  const selectedArchitecture = architectures.find((arch) => arch.id === architectureId);

  /**
   * Obtenir l'environnement sélectionnée
   */
  const selectedEnvironment = environments.find((env) => env.id === environmentId);

  /**
   * Obtenir la version sélectionnée
   */
  const selectedVersion = versions.find((v) => v.id === versionId);

  if (initialLoading) {
    return (
      <DashboardLayout>
        <div className="p-8 text-gray-900 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading execution data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 text-gray-900">
      {/* Header */}
      <div className="mb-6">
        <button className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2" onClick={() => navigate('/executions')}>
          ← Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Create New Execution</h1>
      </div>

      {/* Form */}
      <form className="execution-form" onSubmit={handleSubmit}>
        {/* Action Selection */}
        <div className="form-section">
          <h2>1. Select Action</h2>
          <div className="action-selector">
            <label className={`action-card ${action === 'plan' ? 'active' : ''}`}>
              <input
                type="radio"
                name="action"
                value="plan"
                checked={action === 'plan'}
                onChange={(e) => setAction(e.target.value as 'plan')}
              />
              <div className="action-icon">📋</div>
              <div className="action-name">Plan</div>
              <div className="action-description">
                Preview infrastructure changes without applying them
              </div>
            </label>

            <label className={`action-card ${action === 'apply' ? 'active' : ''}`}>
              <input
                type="radio"
                name="action"
                value="apply"
                checked={action === 'apply'}
                onChange={(e) => setAction(e.target.value as 'apply')}
              />
              <div className="action-icon">🚀</div>
              <div className="action-name">Apply</div>
              <div className="action-description">
                Execute and apply infrastructure changes
              </div>
            </label>

            <label className={`action-card ${action === 'destroy' ? 'active' : ''}`}>
              <input
                type="radio"
                name="action"
                value="destroy"
                checked={action === 'destroy'}
                onChange={(e) => { setAction(e.target.value as 'destroy'); setDestroyConfirmText(''); }}
              />
              <div className="action-icon">🗑️</div>
              <div className="action-name">Destroy</div>
              <div className="action-description">
                Tear down and remove infrastructure resources
              </div>
            </label>
          </div>
        </div>

        {/* Architecture & Version */}
        <div className="form-section">
          <h2>2. Architecture & Version</h2>
          <div className="form-row">
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Architecture *</label>

              {/* Custom Select Button */}
              <div
                onClick={() => architectures.length > 0 && setShowArchitectureDropdown(!showArchitectureDropdown)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: architectures.length === 0 ? '#f3f4f6' : '#ffffff',
                  cursor: architectures.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '14px',
                  color: architectureId ? '#111827' : '#6b7280'
                }}
              >
                <span>
                  {architectureId
                    ? `${selectedArchitecture?.name} (${selectedArchitecture?.provider})`
                    : architectures.length === 0
                    ? 'No architectures available'
                    : 'Select Architecture'}
                </span>
                <span style={{ fontSize: '12px' }}>
                  {showArchitectureDropdown ? '▲' : '▼'}
                </span>
              </div>

              {/* Custom Dropdown */}
              {showArchitectureDropdown && architectures.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    zIndex: 1000,
                    maxHeight: '400px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Search Box */}
                  <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                    <input
                      type="text"
                      placeholder="Rechercher une architecture..."
                      value={architectureSearchTerm}
                      onChange={(e) => setArchitectureSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Architecture List */}
                  <div style={{ overflowY: 'auto', maxHeight: '320px' }}>
                    {/* Architectures groupées par projet */}
                    {Object.values(architecturesByProject).map(({ project, architectures: projectArchs }) => (
                      <div key={project.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {/* Project Header */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProject(project.id);
                          }}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: '#f9fafb',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#374151',
                            userSelect: 'none'
                          }}
                        >
                          <span style={{ fontSize: '10px' }}>
                            {expandedProjects[project.id] ? '▼' : '▶'}
                          </span>
                          <span>📁</span>
                          <span>{project.name.toUpperCase()} ({projectArchs.length})</span>
                        </div>

                        {/* Project Architectures */}
                        {expandedProjects[project.id] && (
                          <div>
                            {projectArchs.map((arch) => (
                              <div
                                key={arch.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setArchitectureId(arch.id);
                                  setVersionId('');
                                  setShowArchitectureDropdown(false);
                                  setArchitectureSearchTerm('');
                                }}
                                style={{
                                  padding: '10px 12px 10px 40px',
                                  cursor: 'pointer',
                                  backgroundColor: architectureId === arch.id ? '#eff6ff' : '#ffffff',
                                  fontSize: '13px',
                                  color: '#111827',
                                  transition: 'background-color 0.15s',
                                  borderLeft: architectureId === arch.id ? '3px solid #3b82f6' : '3px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                  if (architectureId !== arch.id) {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (architectureId !== arch.id) {
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                  }
                                }}
                              >
                                <div style={{ fontWeight: 500 }}>{arch.name}</div>
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                  {arch.provider}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Architectures sans projet */}
                    {architecturesWithoutProject.length > 0 && (
                      <div style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {/* Sans Projet Header */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProject('no-project');
                          }}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: '#f9fafb',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#374151',
                            userSelect: 'none'
                          }}
                        >
                          <span style={{ fontSize: '10px' }}>
                            {expandedProjects['no-project'] ? '▼' : '▶'}
                          </span>
                          <span>📋</span>
                          <span>SANS PROJET ({architecturesWithoutProject.length})</span>
                        </div>

                        {/* Sans Projet Architectures */}
                        {expandedProjects['no-project'] && (
                          <div>
                            {architecturesWithoutProject.map((arch) => (
                              <div
                                key={arch.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setArchitectureId(arch.id);
                                  setVersionId('');
                                  setShowArchitectureDropdown(false);
                                  setArchitectureSearchTerm('');
                                }}
                                style={{
                                  padding: '10px 12px 10px 40px',
                                  cursor: 'pointer',
                                  backgroundColor: architectureId === arch.id ? '#eff6ff' : '#ffffff',
                                  fontSize: '13px',
                                  color: '#111827',
                                  transition: 'background-color 0.15s',
                                  borderLeft: architectureId === arch.id ? '3px solid #3b82f6' : '3px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                  if (architectureId !== arch.id) {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (architectureId !== arch.id) {
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                  }
                                }}
                              >
                                <div style={{ fontWeight: 500 }}>{arch.name}</div>
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                  {arch.provider}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* No Results */}
                    {Object.keys(architecturesByProject).length === 0 && architecturesWithoutProject.length === 0 && (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                        Aucune architecture trouvée
                      </div>
                    )}
                  </div>
                </div>
              )}

              {architectures.length === 0 && (
                <div className="field-hint warning" style={{ marginTop: '8px', color: '#DC2626', fontSize: '12px' }}>
                  No architectures found. Please create an architecture first.
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Version *</label>
              <select
                value={versionId}
                onChange={(e) => setVersionId(e.target.value)}
                required
                disabled={!architectureId || versionsLoading || versions.length === 0}
              >
                <option value="">
                  {!architectureId
                    ? 'Select an architecture first'
                    : versionsLoading
                    ? 'Loading versions...'
                    : versions.length === 0
                    ? 'No versions available'
                    : 'Select Version'}
                </option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    v{version.version_number} - {version.commit_message}
                  </option>
                ))}
              </select>
              {selectedVersion && (
                <div className="field-hint" style={{ marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>
                  Created {new Date(selectedVersion.created_at).toLocaleDateString()} by{' '}
                  {selectedVersion.author_name}
                </div>
              )}
              {architectureId && !versionsLoading && versions.length === 0 && (
                <div className="field-hint warning" style={{ marginTop: '8px', color: '#DC2626', fontSize: '12px' }}>
                  No versions found for this architecture. Please create a version first by saving the architecture in the Canvas editor.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Environment */}
        <div className="form-section">
          <h2>3. Target Environment</h2>
          <div className="form-group">
            <label>Environment *</label>
            <select
              value={environmentId}
              onChange={(e) => setEnvironmentId(e.target.value)}
              required
              disabled={environments.length === 0}
            >
              <option value="">
                {environments.length === 0 ? 'No environments available' : 'Select Environment'}
              </option>
              {environments.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name} ({env.type})
                  {env.requiresApproval && ' - Requires Approval'}
                </option>
              ))}
            </select>
            {environments.length === 0 && (
              <div className="field-hint warning" style={{ marginTop: '8px', color: '#DC2626', fontSize: '12px' }}>
                No environments found. Please create an environment first.
              </div>
            )}
            {selectedEnvironment && (
              <div className="environment-info">
                <span className={`env-badge env-${selectedEnvironment.type}`}>
                  {selectedEnvironment.type}
                </span>
                {selectedEnvironment.requiresApproval && (
                  <span className="warning-badge">⚠️ Requires Approval</span>
                )}
                {selectedEnvironment.description && (
                  <p className="env-description">{selectedEnvironment.description}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Plan Selection (for Apply) */}
        {action === 'apply' && (
          <div className="form-section">
            <h2>4. Select Plan to Apply</h2>
            <div className="form-group">
              <label>Plan Run *</label>
              <select
                value={planRunId}
                onChange={(e) => setPlanRunId(e.target.value)}
                required
              >
                <option value="">Select a plan run</option>
                {recentPlans.map((plan) => (
                  <option key={plan.run_id} value={plan.run_id}>
                    {plan.run_id} - v{plan.version_number} -{' '}
                    {new Date(plan.created_at).toLocaleString()}
                  </option>
                ))}
              </select>
              {recentPlans.length === 0 && architectureId && environmentId && (
                <div className="field-hint warning">
                  No successful plan runs found. Create a plan first.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Parameters Resolution */}
        <div className="form-section">
          <h2>{action === 'apply' ? '5' : '4'}. Parameters</h2>

          {parametersLoading ? (
            <div className="field-hint">Loading parameters...</div>
          ) : resolvedParameters.length === 0 ? (
            <div className="field-hint">
              No parameters defined for this architecture. Parameters can be configured in the Variables panel.
            </div>
          ) : (
            <div className="parameters-list">
              <div className="field-hint" style={{ marginBottom: '12px' }}>
                Parameters are resolved with cascade: Architecture default → Environment override → Execution override
              </div>

              {resolvedParameters.map((param) => (
                <div key={param.name} className="parameter-item" style={{
                  marginBottom: '16px',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '14px' }}>
                        {param.displayName || param.name}
                      </label>
                      {param.description && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                          {param.description}
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: param.source === 'default' ? '#dbeafe' :
                        param.source === 'environment_override' ? '#dcfce7' : '#fef3c7',
                      color: param.source === 'default' ? '#1e40af' :
                        param.source === 'environment_override' ? '#166534' : '#92400e'
                    }}>
                      {param.source === 'default' ? 'Default' :
                        param.source === 'environment_override' ? 'Env Override' : 'Exec Override'}
                    </span>
                  </div>

                  <div style={{ marginBottom: '8px', fontSize: '13px', color: '#374151' }}>
                    <strong>Current value:</strong> {JSON.stringify(param.value)}
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                      Override for this execution:
                    </label>
                    {param.type === 'boolean' ? (
                      <select
                        value={executionOverrides[param.name] !== undefined ? String(executionOverrides[param.name]) : ''}
                        onChange={(e) => {
                          const newOverrides = { ...executionOverrides };
                          if (e.target.value === '') {
                            delete newOverrides[param.name];
                          } else {
                            newOverrides[param.name] = e.target.value === 'true';
                          }
                          setExecutionOverrides(newOverrides);
                        }}
                        style={{
                          width: '100%',
                          marginTop: '4px',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}
                      >
                        <option value="">Use resolved value</option>
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    ) : param.type === 'number' ? (
                      <input
                        type="number"
                        value={executionOverrides[param.name] ?? ''}
                        onChange={(e) => {
                          const newOverrides = { ...executionOverrides };
                          if (e.target.value === '') {
                            delete newOverrides[param.name];
                          } else {
                            newOverrides[param.name] = Number(e.target.value);
                          }
                          setExecutionOverrides(newOverrides);
                        }}
                        placeholder={`Use resolved value: ${param.value}`}
                        style={{
                          width: '100%',
                          marginTop: '4px',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}
                      />
                    ) : (
                      <input
                        type="text"
                        value={executionOverrides[param.name] ?? ''}
                        onChange={(e) => {
                          const newOverrides = { ...executionOverrides };
                          if (e.target.value === '') {
                            delete newOverrides[param.name];
                          } else {
                            newOverrides[param.name] = e.target.value;
                          }
                          setExecutionOverrides(newOverrides);
                        }}
                        placeholder={`Use resolved value: ${param.value}`}
                        style={{
                          width: '100%',
                          marginTop: '4px',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && <div className="form-error">⚠️ {error}</div>}

        {/* Validation errors */}
        {validationErrors && validationErrors.length > 0 && (
          <div className="form-error" style={{ textAlign: 'left' }}>
            <strong>⚠️ Architecture invalide — corrigez les erreurs avant de lancer le plan :</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {validationErrors.map((e, i) => (
                <li key={i} style={{ marginBottom: '4px' }}>
                  <strong>{e.label}</strong>
                  {e.resourceType && <span style={{ color: '#9ca3af', fontSize: '12px' }}> ({e.resourceType})</span>}
                  {e.field && <span> — champ requis manquant : <code>{e.field}</code></span>}
                  {!e.field && <span> — {e.message}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Summary */}
        {architectureId && versionId && environmentId && (
          <div className="execution-summary">
            <h3>Execution Summary</h3>
            <div className="summary-row">
              <strong>Action:</strong> <span className="action-badge-large">{action.toUpperCase()}</span>
            </div>
            <div className="summary-row">
              <strong>Architecture:</strong> {selectedArchitecture?.name}
              {selectedArchitecture?.description && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {selectedArchitecture.description}
                </div>
              )}
            </div>
            <div className="summary-row">
              <strong>Version:</strong> v{selectedVersion?.version_number}
              {selectedVersion?.commit_message && (
                <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                  - {selectedVersion.commit_message}
                </span>
              )}
            </div>
            <div className="summary-row">
              <strong>Environment:</strong> {selectedEnvironment?.name} ({selectedEnvironment?.type})
            </div>
            {action === 'apply' && planRunId && (
              <div className="summary-row">
                <strong>Plan Run:</strong> <code>{planRunId}</code>
              </div>
            )}
            {selectedEnvironment?.requiresApproval && action === 'apply' && (
              <div className="summary-warning">
                ⚠️ This execution will require approval before running
              </div>
            )}
          </div>
        )}

        {/* Reset Tfstate */}
        {architectureId && environmentId && (
          <div style={{ marginTop: '16px', padding: '12px 16px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#92400e' }}>⚠️ Réinitialiser le Terraform State</div>
                <div style={{ fontSize: '12px', color: '#78350f', marginTop: '2px' }}>
                  Utilisez uniquement si des ressources ont été supprimées hors GenIaC et que le state est obsolète.
                </div>
              </div>
              <button
                type="button"
                onClick={handleResetTfstate}
                disabled={resetStateLoading}
                style={{ flexShrink: 0, padding: '6px 14px', fontSize: '12px', fontWeight: 600, color: '#92400e', background: 'white', border: '1px solid #f59e0b', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {resetStateLoading ? 'Réinitialisation...' : 'Reset State'}
              </button>
            </div>
            {resetStateMessage && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: resetStateMessage.type === 'success' ? '#15803d' : '#dc2626', fontWeight: 500 }}>
                {resetStateMessage.type === 'success' ? '✅' : '❌'} {resetStateMessage.text}
              </div>
            )}
          </div>
        )}

        {/* Destroy confirmation */}
        {action === 'destroy' && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderLeft: '4px solid #ef4444',
            borderRadius: '6px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <strong style={{ color: '#991b1b', fontSize: '14px' }}>
                Destructive action — this will permanently delete all infrastructure resources
              </strong>
            </div>
            <p style={{ color: '#7f1d1d', fontSize: '13px', margin: '0 0 12px 0' }}>
              Terraform will destroy all resources managed by this architecture in the selected environment.
              This action <strong>cannot be undone</strong>. The Terraform state will be cleared.
            </p>
            <label style={{ fontSize: '13px', color: '#7f1d1d', display: 'block', marginBottom: '6px' }}>
              Type the architecture name to confirm:{' '}
              <strong>{architectures.find(a => a.id === architectureId)?.name || '—'}</strong>
            </label>
            <input
              type="text"
              value={destroyConfirmText}
              onChange={e => setDestroyConfirmText(e.target.value)}
              placeholder="Type architecture name here..."
              style={{
                width: '100%',
                padding: '8px 10px',
                border: destroyConfirmText === (architectures.find(a => a.id === architectureId)?.name || '\0')
                  ? '1px solid #22c55e' : '1px solid #fca5a5',
                borderRadius: '4px',
                fontSize: '13px',
                background: '#fff',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/executions')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={action === 'destroy' ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={loading || (action === 'destroy' && destroyConfirmText !== (architectures.find(a => a.id === architectureId)?.name || '\0'))}
            style={action === 'destroy' ? { background: '#ef4444', borderColor: '#dc2626', color: '#fff' } : {}}
          >
            {loading ? 'Creating...' : action === 'destroy' ? '🗑️ Confirm Destroy' : `Create ${action.charAt(0).toUpperCase() + action.slice(1)}`}
          </button>
        </div>
      </form>
    </div>
    </DashboardLayout>
  );
};

export default NewExecutionPage;
